import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Copy, Eye, EyeSlash, Key, Warning } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import KitModal from '../../components/UI/KitModal';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { MasterApiKeyService } from '../../service/MasterApiKeyService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import {
  MASTER_KEY_OWNER_EMAIL,
  MasterApiKeyStatus,
} from '../../util/Types/MasterApiKey';

const DANGER = 'rgba(240, 84, 80, 1)';

const MasterApiKeyPage: React.FC = () => {
  const authUser = useAuthStore(state => state.user);
  const setNotification = useNotificationStore(state => state.setNotification);

  const isOwner = authUser?.email?.toLowerCase() === MASTER_KEY_OWNER_EMAIL;

  const [status, setStatus] = useState<MasterApiKeyStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(true);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const result = await MasterApiKeyService.getStatus();
      setStatus(result);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as string),
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      loadStatus();
    } else {
      setLoadingStatus(false);
    }
  }, [isOwner]);

  const rotate = async () => {
    setRotating(true);
    try {
      const result = await MasterApiKeyService.rotate();
      setRevealedKey(result.apiKey);
      setShowKey(true);
      setShowConfirm(false);
      setNotification({
        type: NotificationType.Success,
        message: 'Master API key generated - copy it now, it is shown once',
      });
      loadStatus();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as string),
      });
    } finally {
      setRotating(false);
    }
  };

  const copyKey = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setNotification({
        type: NotificationType.Success,
        message: 'Master API key copied to clipboard',
      });
    } catch {
      setNotification({
        type: NotificationType.Error,
        message: 'Failed to copy to clipboard',
      });
    }
  };

  if (!isOwner) {
    return (
      <Box sx={{ maxWidth: 640, margin: '60px auto', textAlign: 'center' }}>
        <Typography variant='h6'>Master API Key</Typography>
        <Typography sx={{ color: 'rgba(71, 84, 103, 1)', marginTop: '12px' }}>
          Only the master key owner can view or manage this key.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, margin: '48px auto', padding: '0 16px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '8px',
        }}
      >
        <Key size={28} />
        <Typography variant='h5'>Master API Key</Typography>
      </Box>
      <Typography sx={{ color: 'rgba(71, 84, 103, 1)', marginBottom: '24px' }}>
        A single privileged key to call the API on behalf of any customer. Use
        it in the <code>api-key</code> header, with{' '}
        <code>x-flux-act-as-space</code> to act as a customer's space. Every use
        is audited.
      </Typography>

      <Paper variant='outlined' sx={{ padding: '24px' }}>
        {loadingStatus ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box sx={{ marginBottom: '20px' }}>
              <Typography sx={{ fontWeight: 600 }}>Status</Typography>
              {status?.exists ? (
                <Box sx={{ color: 'rgba(71, 84, 103, 1)', marginTop: '6px' }}>
                  <Typography>
                    Active key: <code>{status.prefix}…</code>{' '}
                    {status.isActive ? '(active)' : '(inactive)'}
                  </Typography>
                  <Typography>
                    Last rotated:{' '}
                    {status.lastRotatedAt
                      ? new Date(status.lastRotatedAt).toLocaleString()
                      : '—'}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  sx={{ color: 'rgba(71, 84, 103, 1)', marginTop: '6px' }}
                >
                  No key has been generated yet.
                </Typography>
              )}
            </Box>

            <Button
              variant='contained'
              onClick={() => setShowConfirm(true)}
              disabled={rotating}
            >
              {status?.exists ? 'Rotate key' : 'Generate key'}
            </Button>
          </>
        )}
      </Paper>

      {showConfirm && (
        <KitModal show={true} onHide={() => setShowConfirm(false)}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px 60px 20px',
            }}
          >
            <Warning size={80} color={DANGER} />
            <Typography sx={{ fontWeight: 500, marginTop: '10px' }}>
              {status?.exists
                ? 'Rotate master API key'
                : 'Generate master API key'}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(71, 84, 103, 1)',
                width: '80%',
                margin: '20px 0',
                textAlign: 'center',
                lineHeight: '28px',
              }}
            >
              {status?.exists
                ? 'This immediately invalidates the current key. Any integration still using it will stop working. The new secret is shown only once.'
                : 'This creates the master key. The secret is shown only once — copy it immediately.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: '20px' }}>
              <Button
                variant='outlined'
                onClick={() => setShowConfirm(false)}
                disabled={rotating}
              >
                Go Back
              </Button>
              <Button
                variant='outlined'
                sx={{
                  border: `1px solid ${DANGER}`,
                  color: DANGER,
                  background: 'transparent',
                  '&:hover': {
                    border: `1px solid ${DANGER}`,
                    color: DANGER,
                    background: 'transparent',
                  },
                }}
                onClick={rotate}
                disabled={rotating}
              >
                {rotating ? (
                  <CircularProgress size={20} />
                ) : status?.exists ? (
                  'Rotate Key'
                ) : (
                  'Generate Key'
                )}
              </Button>
            </Box>
          </Box>
        </KitModal>
      )}

      {revealedKey && (
        <KitModal
          show={true}
          onHide={() => setRevealedKey(null)}
          title='Your master API key'
        >
          <Box sx={{ padding: '24px 32px 32px' }}>
            <Typography sx={{ color: DANGER, marginBottom: '16px' }}>
              Copy this key now. It is shown only once and cannot be retrieved
              again.
            </Typography>
            <TextField
              fullWidth
              value={revealedKey}
              type={showKey ? 'text' : 'password'}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <Box
                      sx={{ cursor: 'pointer', display: 'flex' }}
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? <EyeSlash /> : <Eye />}
                    </Box>
                    <Box
                      sx={{ cursor: 'pointer', display: 'flex', marginLeft: 1 }}
                      onClick={copyKey}
                    >
                      <Copy />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '24px',
              }}
            >
              <Button variant='contained' onClick={() => setRevealedKey(null)}>
                Done
              </Button>
            </Box>
          </Box>
        </KitModal>
      )}
    </Box>
  );
};

export default MasterApiKeyPage;
